import { formatDate, formatCurrency } from './helpers';
import { state } from './app';

declare const jspdf: any;

const COMPANY_DEFAULTS = {
  name: 'Gravant Labs LLC',
  address: '1201 Orange Street, Suite 600',
  city: 'Wilmington, DE 19801',
  country: 'United States',
  ein: '35-2952235',
  phone: '',
  email: 'billing@gravantlabs.com',
  website: 'gravantlabs.com',
  portal: 'customers.gravantlabs.com',
};

function getCompany() {
  const u = state.user;
  return {
    ...COMPANY_DEFAULTS,
    phone: u?.phone || COMPANY_DEFAULTS.phone,
    email: u?.email || COMPANY_DEFAULTS.email,
  };
}

export function generateInvoicePDF(inv: any, pay?: any) {
  const doc = new jspdf.jsPDF();
  const COMPANY = getCompany();
  const items = typeof inv.items === 'string' ? JSON.parse(inv.items) : inv.items || [];
  const pageW = doc.internal.pageSize.getWidth();
  let y = 20;

  const pageH = doc.internal.pageSize.getHeight();

  // ─── Header: Seller ───
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(184, 150, 12);
  doc.text(COMPANY.name, 20, y);

  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text(COMPANY.address, 20, y + 7);
  doc.text(COMPANY.city, 20, y + 12);
  doc.text('EIN: ' + COMPANY.ein + '  |  ' + COMPANY.phone, 20, y + 17);
  doc.text(COMPANY.email + '  |  ' + COMPANY.website, 20, y + 22);

  // ─── Header: Invoice meta (right side) ───
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 30, 30);
  doc.text('INVOICE', pageW - 20, y, { align: 'right' });
  doc.setFontSize(11);
  doc.text(inv.invoice_number || '', pageW - 20, y + 7, { align: 'right' });

  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text('Date: ' + formatDate(inv.issue_date), pageW - 20, y + 14, { align: 'right' });
  if (inv.due_date) doc.text('Due: ' + formatDate(inv.due_date), pageW - 20, y + 19, { align: 'right' });

  // Payment terms
  if (inv.issue_date && inv.due_date) {
    const issued = new Date(inv.issue_date);
    const due = new Date(inv.due_date);
    const netDays = Math.round((due.getTime() - issued.getTime()) / (1000 * 60 * 60 * 24));
    if (netDays > 0) doc.text('Terms: Net ' + netDays, pageW - 20, y + 24, { align: 'right' });
  }

  // Status badge
  if (inv.status === 'paid') {
    doc.setFillColor(45, 184, 127);
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    const badgeX = pageW - 50;
    const badgeY = y + (inv.due_date ? 29 : 24);
    doc.roundedRect(badgeX, badgeY - 4, 30, 8, 2, 2, 'F');
    doc.text('PAID', badgeX + 15, badgeY + 1, { align: 'center' });
    if (inv.paid_at) {
      doc.setTextColor(100, 100, 100);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.text('Paid: ' + formatDate(inv.paid_at), pageW - 20, badgeY + 9, { align: 'right' });
    }
  }

  // ─── Divider ───
  const dividerOffset = (inv.status === 'paid' && inv.paid_at) ? 45 : (inv.status === 'paid' ? 38 : 30);
  y += dividerOffset;
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  doc.line(20, y, pageW - 20, y);
  y += 10;

  // ─── Bill To ───
  doc.setFontSize(7.5);
  doc.setTextColor(150, 150, 150);
  doc.setFont('helvetica', 'bold');
  doc.text('BILL TO', 20, y);
  if (inv.project_title) doc.text('PROJECT', pageW / 2, y);
  y += 6;

  // Each column wraps within its own half-width so long values flow onto the next
  // line instead of overflowing the page; the taller column wins before the items table.
  const colW = pageW / 2 - 25;
  doc.setFontSize(10);
  doc.setTextColor(30, 30, 30);
  doc.setFont('helvetica', 'bold');
  const nameLines = doc.splitTextToSize(inv.client_name || '', colW);
  doc.text(nameLines, 20, y);
  let projectBottom = y;
  if (inv.project_title) {
    doc.setFont('helvetica', 'normal');
    const titleLines = doc.splitTextToSize(inv.project_title, colW);
    doc.text(titleLines, pageW / 2, y);
    projectBottom = y + titleLines.length * 4.5;
  }
  y += nameLines.length * 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(80, 80, 80);
  const billField = (txt: string) => {
    const ls = doc.splitTextToSize(txt, colW);
    doc.text(ls, 20, y);
    y += ls.length * 4.5;
  };
  if (inv.client_company) billField(inv.client_company);
  if (inv.client_email) billField(inv.client_email);
  if (inv.client_address) billField(inv.client_address);
  if (inv.client_country) billField(inv.client_country);
  if (inv.client_tax_id) billField('Tax ID: ' + inv.client_tax_id);

  y = Math.max(y, projectBottom);
  y += 8;

  // ─── Items table ───
  const cols = [20, 110, 135, 165];
  doc.setFillColor(248, 248, 245);
  doc.rect(20, y - 4, pageW - 40, 8, 'F');
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(100, 100, 100);
  doc.text('DESCRIPTION', cols[0], y);
  doc.text('QTY', cols[1], y);
  doc.text('RATE', cols[2], y);
  doc.text('AMOUNT', cols[3], y);
  y += 8;

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(50, 50, 50);
  doc.setFontSize(9);
  items.forEach((item: any) => {
    if (y > 255) { doc.addPage(); y = 20; }
    const desc = String(item.description || '');
    const lines = doc.splitTextToSize(desc, 85);
    doc.text(lines, cols[0], y);
    doc.text(String(item.quantity || 1), cols[1], y);
    doc.text(formatCurrency(item.rate || item.unit_price), cols[2], y);
    doc.text(formatCurrency(item.amount || item.total || (item.quantity || 1) * (item.rate || item.unit_price || 0)), cols[3], y);
    y += Math.max(lines.length * 4.5, 6);
  });

  y += 4;
  doc.setDrawColor(220, 220, 220);
  doc.line(cols[2] - 5, y, pageW - 20, y);
  y += 6;

  // ─── Totals ───
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  doc.text('Subtotal', cols[2], y);
  doc.text(formatCurrency(inv.subtotal), pageW - 20, y, { align: 'right' });
  y += 5.5;
  if (inv.tax_amount) {
    doc.text('Tax (' + (inv.tax_rate || 0) + '%)', cols[2], y);
    doc.text(formatCurrency(inv.tax_amount), pageW - 20, y, { align: 'right' });
    y += 5.5;
  }
  if (inv.discount) {
    doc.text('Discount', cols[2], y);
    doc.text('-' + formatCurrency(inv.discount), pageW - 20, y, { align: 'right' });
    y += 5.5;
  }
  doc.setDrawColor(200, 200, 200);
  doc.line(cols[2] - 5, y, pageW - 20, y);
  y += 7;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(30, 30, 30);
  doc.text('TOTAL', cols[2], y);
  doc.text(formatCurrency(inv.total, inv.currency) + ' ' + (inv.currency || 'USD'), pageW - 20, y, { align: 'right' });

  // ─── Paid Via (how the invoice was paid) ───
  if (inv.status === 'paid' && pay) {
    let method: string;
    if (pay.payment_provider === 'stripe') {
      if (pay.card_brand) {
        method = 'Card - ' + pay.card_brand.charAt(0).toUpperCase() + pay.card_brand.slice(1) + (pay.card_last4 ? ' ****' + pay.card_last4 : '');
      } else {
        method = 'Stablecoin (USDC) - via Stripe';
      }
    } else if (pay.payment_provider === 'crypto') {
      method = (pay.crypto_currency || 'Stablecoin') + (pay.network ? ' (' + pay.network + ')' : '');
    } else {
      method = 'Bank transfer';
    }
    const ref = pay.stripe_payment_intent || pay.tx_hash || '';
    y += 14;
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(150, 150, 150);
    doc.text('PAID VIA', 20, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.setFontSize(8.5);
    doc.text('Method: ' + method, 20, y); y += 4.5;
    if (pay.confirmed_at) { doc.text('Paid on: ' + formatDate(pay.confirmed_at), 20, y); y += 4.5; }
    if (ref) {
      const refLines = doc.splitTextToSize('Reference: ' + ref, pageW - 40);
      doc.text(refLines, 20, y); y += refLines.length * 4.5;
    }
  }

  // ─── Payment Instructions ───
  if (inv.payment_instructions) {
    y += 14;
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(150, 150, 150);
    doc.text('PAYMENT', 20, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.setFontSize(8.5);
    doc.text(doc.splitTextToSize(inv.payment_instructions, pageW - 40), 20, y);
    y += doc.splitTextToSize(inv.payment_instructions, pageW - 40).length * 4.5;
  }

  // ─── Notes ───
  if (inv.notes) {
    y += 14;
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(150, 150, 150);
    doc.text('NOTES', 20, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.setFontSize(8.5);
    const lines = doc.splitTextToSize(inv.notes, pageW - 40);
    doc.text(lines, 20, y);
  }

  // ─── Verification with QR ───
  if (inv.verification_hash) {
    const verifyY = y > 230 ? (doc.addPage(), 20) : y + 14;
    const verifyUrl = 'https://customers.gravantlabs.com/verify/' + inv.invoice_number;

    doc.setDrawColor(220, 220, 220);
    doc.line(20, verifyY, pageW - 20, verifyY);

    // QR Code
    const qrGen = (window as any).qrcode;
    if (qrGen) {
      const qr = qrGen(0, 'M');
      qr.addData(verifyUrl);
      qr.make();
      const qrSize = 28;
      const qrX = pageW - 20 - qrSize;
      const qrDataUrl = qr.createDataURL(2, 0);
      doc.addImage(qrDataUrl, 'PNG', qrX, verifyY + 3, qrSize, qrSize);
    }

    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(100, 100, 100);
    doc.text('DIGITAL VERIFICATION', 20, verifyY + 6);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(130, 130, 130);
    doc.text('SHA-256: ' + inv.verification_hash, 20, verifyY + 11);
    doc.text('Verify: ' + verifyUrl, 20, verifyY + 15.5);
    doc.text('Scan QR code to verify this invoice', 20, verifyY + 20);
  }

  // ─── Footer ───
  const footerY = doc.internal.pageSize.getHeight() - 20;
  doc.setDrawColor(220, 220, 220);
  doc.line(20, footerY - 5, pageW - 20, footerY - 5);
  doc.setFontSize(7.5);
  doc.setTextColor(160, 160, 160);
  doc.text(COMPANY.name + '  |  EIN: ' + COMPANY.ein + '  |  ' + COMPANY.phone + '  |  ' + COMPANY.website, pageW / 2, footerY, { align: 'center' });
  doc.text('Digitally verified invoice. Verify at ' + COMPANY.portal + '/verify', pageW / 2, footerY + 4.5, { align: 'center' });

  // ─── PAID watermark (top layer — drawn last so it appears above all content) ───
  if (inv.status === 'paid') {
    doc.setTextColor(184, 150, 12);
    doc.setFontSize(85);
    doc.setFont('helvetica', 'bold');
    doc.setGState(new doc.GState({ opacity: 0.18 }));
    doc.text('PAID', pageW / 2, pageH / 2, { align: 'center', angle: 35 });
    doc.setGState(new doc.GState({ opacity: 1 }));
  }

  doc.save((inv.invoice_number || 'invoice') + '.pdf');
}
